// useOpenJobFromHistory — opens a conversation into the main thread from
// history. Shared by the JobsSection sidebar row click and the command palette
// "Recent threads" group so both restore identically (token → store → URL sync
// to /c/<conversationId> when the job carries one; legacy jobs without one load
// in place). Pass the whole conversation group to restore every turn; pass a
// lone job to restore just it. No-op when no tenant is active; failures are
// caught + logged.

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useAccessToken } from "@/auth/useAccessToken";
import { useChatStore } from "@/runtime/chatStore";
import type { JobSnapshot } from "@/types";

export function useOpenJobFromHistory(): (
  job: JobSnapshot,
  conversationJobs?: readonly JobSnapshot[],
) => Promise<void> {
  const activeTenantId = useChatStore((s) => s.activeTenantId);
  const loadJobFromHistory = useChatStore((s) => s.loadJobFromHistory);
  const loadConversationFromHistory = useChatStore((s) => s.loadConversationFromHistory);
  const navigate = useNavigate();
  const { acquireToken } = useAccessToken();

  return useCallback(
    async (job: JobSnapshot, conversationJobs?: readonly JobSnapshot[]) => {
      if (!activeTenantId) return;
      try {
        const token = await acquireToken();
        const req = job.request_jsonb as Record<string, unknown> | null | undefined;
        const conversationId =
          req && typeof req === "object" && typeof req.conversation_id === "string"
            ? (req.conversation_id as string)
            : null;
        // `conversationJobs` is the group the caller already rendered and
        // badged, oldest-first, so the thread we open and the turn count on the
        // row agree by construction. Callers that know of only one job (and
        // every existing test) keep the single-turn path byte-for-byte.
        if (conversationJobs && conversationJobs.length > 1) {
          await loadConversationFromHistory(
            {
              tenantId: activeTenantId,
              conversationId,
              turns: conversationJobs.map((turn) => ({
                jobId: turn.job_id,
                queryText: turn.query_text ?? "",
              })),
            },
            token,
          );
        } else {
          await loadJobFromHistory(
            {
              tenantId: activeTenantId,
              jobId: job.job_id,
              conversationId,
              queryText: job.query_text ?? "",
            },
            token,
          );
        }
        if (conversationId) {
          navigate(`/c/${conversationId}`);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[useOpenJobFromHistory] failed", err);
      }
    },
    [activeTenantId, acquireToken, loadConversationFromHistory, loadJobFromHistory, navigate],
  );
}
